import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Users, Award, Target, Building2 } from 'lucide-react'

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const team = [
    {
      name: "Hümeyra Ergin Işık",
      role: "CEO & Founder",
      expertise: "Energy Technology Innovation"
    },
    {
      name: "Ercan Işık",
      role: "CTO",
      expertise: "Photoluminescent Materials"
    },
    {
      name: "Esra Demir",
      role: "VP of Engineering",
      expertise: "Product Development"
    },
    {
      name: "Sema Toklucu",
      role: "VP of Operations",
      expertise: "Manufacturing & Quality"
    }
  ]

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            About <span className="text-blue-500">G-GES</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            G-GES Sürdürülebilir Enerji Çözümleri A.Ş. is a pioneering company in sustainable energy solutions, 
            revolutionizing lighting technology with our breakthrough Pigmentium™ innovation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="text-3xl font-bold text-gray-800 mb-6">Our Mission</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              We are dedicated to creating sustainable lighting solutions that eliminate the need for traditional 
              electrical power while providing superior performance and reliability. Our Pigmentium™ technology 
              represents a paradigm shift in how we think about energy storage and lighting.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <Target className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-800">5+ Years</div>
                <div className="text-sm text-gray-600">R&D Development</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <Award className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-800">50+</div>
                <div className="text-sm text-gray-600">Patents Pending</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8"
          >
            <Building2 className="w-12 h-12 text-blue-500 mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Company Information</h3>
            <div className="space-y-3 text-gray-600">
              <p><strong>Founded:</strong> 2019</p>
              <p><strong>Location:</strong> İstanbul, Turkey</p>
              <p><strong>Industry:</strong> Sustainable Energy Technology</p>
              <p><strong>Specialization:</strong> Photoluminescent Lighting Systems</p>
              <p><strong>Headquarters:</strong> Pendik, İstanbul</p>
              <p><strong>Phone:</strong> 0850 309 7307</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">Our Leadership Team</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="text-center p-6 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">{member.name}</h4>
                <p className="text-blue-600 font-medium mb-2">{member.role}</p>
                <p className="text-sm text-gray-600">{member.expertise}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-center bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-8 text-white"
        >
          <h3 className="text-3xl font-bold mb-4">Ready to Partner with Us?</h3>
          <p className="text-xl mb-6 opacity-90">
            Join the sustainable lighting revolution. Contact us to discuss how Pigmentium™ technology 
            can transform your lighting requirements.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-blue-600 font-semibold py-3 px-8 rounded-xl hover:shadow-lg transition-all duration-300"
          >
            Start Your Project Today
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}

export default About 